import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * AI Maturity Index (AIMI) — the solution page.
 *
 * It was an **imported** WordPress page until 2026-08-28
 * (`content/imported/pages/en/solutions__ai-maturity-index.json`, still on disk
 * as the record of what digitopia.co says). The scrape kept the sentences and
 * lost the page: the hero was gone entirely, every heading with it, the
 * Traditional-Consulting-versus-AIMI comparison arrived as one paragraph with
 * the ❌ and ✅ characters inline, and the three customer quotes were welded
 * together with their attributions attached to the wrong speaker. Below that,
 * sixty blog cards the page never asked for.
 *
 * Asked for that day with six screenshots of the live page and four of the
 * pictures on it — *"ai-maturity-index-aimi sayfası oluşturalım… ilgili sayfayı
 * tastamam şekilde, aynısını, tıpkısını tasarla kaydırmalı testimoniallar
 * proven, yazaki, kordsa"*.
 *
 * So it is a written page now, band for band the reference's, expressed in this
 * site's own section vocabulary rather than as bespoke markup: the two-column
 * hero, the customer logo row, "This is what you get", "Why AIMI Matters", the
 * three-step How It Works panel, the comparison with traditional consulting,
 * "Make AI Progress Visible and Measurable" with the platform on a laptop, the
 * three-tab customer quotes and the closing ask.
 *
 * **The copy is digitopia.co's own, verbatim** — taken from the imported JSON
 * rather than transcribed off a screenshot, except for the headings and the
 * hero, which the scrape did not carry and which are read off the reference
 * images.
 *
 * Where this page departs from the reference, and why:
 *
 *  * **All four pictures the request supplied were already in the repository**
 *    — the AIMI briefing in the hero, the applauding leadership team, the
 *    laptop dashboard and the Projects screen are the same files the DAIMI page
 *    draws. Nothing new was committed; the page points at what is there.
 *  * **The comparison band is two lists, one above the other**, not two columns
 *    of red crosses and green ticks. The words are identical and the bullets
 *    are this site's own; a side-by-side tick/cross table would need a section
 *    kind that does not exist yet.
 *  * **The closing ask is two buttons, not a nine-field form.** The reference
 *    ends on a HubSpot form; no form on this site submits anywhere yet, so
 *    reproducing it would mean this page's last call to action silently drops
 *    every lead. Same decision, and the same reasoning, as the DAIMI page.
 *  * **The "Essential Reads on AI Maturity" carousel is a signpost.** The
 *    reference ends on a scrolling row of blog cards; there is no post-carousel
 *    section on this site, so the band is the heading and a link to Impact
 *    Labs, where those articles live.
 */
export const route: RouteDef = {
  id: 'aimiSolution',
  path: 'solutions/ai-maturity-index-aimi',
  priority: 0.9,
}

/** The Impact Platform itself. The platforms consolidate off this hostname. */
const PLATFORM = 'https://impact.digitopia.ai'

export const aimiSolution: WrittenPage = {
  navLabel: 'AI Maturity Index',
  title: 'AI Maturity Index (AIMI)',
  description:
    'AIMI benchmarks where your organization stands on AI, prioritizes the highest value moves and turns them into a roadmap you can execute, all delivered through the Impact Platform.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'AI Maturity Index (AIMI)',
      headline: 'How Ready Is Your Organization for AI?',
      sub: 'Start with an AI maturity assessment to benchmark where you stand and set the right strategy. Maximize the value AI creates for your organization.',
      // `#contact` rather than the Contact Us page, since 2026-08-30: the
      // closing form now sits at the bottom of every document (see
      // `components/ContactBand.tsx`), so this scrolls the reader down to it.
      ctas: [{ label: 'Talk with an expert', href: '#contact' }],
      photo: {
        src: '/photo/aimi-assessment-presentation.webp',
        alt: 'A consultant presenting an AIMI score, the scores by dimension and an industry benchmark to colleagues on a meeting-room screen.',
      },
    },
    {
      kind: 'logos',
      heading: 'Great companies work with Digitopia.',
      items: [
        { name: 'dhiraagu', src: '/logo/dhiraagu.webp' },
        { name: 'DHL', src: '/logo/dhl.webp' },
        { name: 'Domino’s', src: '/logo/dominos.webp' },
        { name: 'Enerjisa', src: '/logo/enerjisa.webp' },
        // Same swap as the DAIMI page's row, and for the same reason: these two
        // are the transparent 300x300 squares rather than the white-boxed
        // canvases, `padded` so the ink is sized like its neighbours'.
        { name: 'Intel', src: '/logo/intelaicard.png', padded: true },
        { name: 'Microsoft', src: '/logo/microsoft.webp' },
        { name: 'PROVEN', src: '/logo/provencard.png', padded: true },
      ],
      check:
        'The reference row carries ten marks and this one carries six, in the '
        + 'reference’s own order. Equatorial Coca-Cola Bottling Company, Gases del '
        + 'Caribe, NKC of America and PSDI have no file in logo-src/ — they are on '
        + 'no other page of this site either. Send the four marks (tight crop, '
        + 'transparent PNG or WebP) and they drop straight into this row.',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'This is what you get',
      sub: 'AIMI gives you clarity, direction, and a growth plan you can execute immediately.',
      points: [
        'AIMI gives you a clear AI maturity baseline.',
        'You see exactly where your AI capabilities stand today.',
        'Benchmarks show your position in your industry.',
        'AI Wizards guide your teams through every step.',
        'You get a prioritized roadmap to scale AI safely and effectively.',
        'The Impact Platform keeps progress visible and measurable.',
      ],
      outro: ['Ready to apply this to your organization?'],
      photo: {
        src: '/photo/boardroom-applause-dashboards.webp',
        alt: 'A leadership team applauding at the end of an assessment session, dashboards on the screen behind them.',
      },
      // Uploaded by Fatih through the panel on 2026-08-30. The photograph
      // above stays: it is the poster this panel holds until the film is on
      // screen, and the fallback for a reader who blocks video or asks for
      // reduced motion.
      video: {
        src: 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/video/d556d28a-8939-49b5-ba7b-3e9368ce07a8-AIMI-2_V4_web_crf23.mp4',
        title: 'A walk through AIMI: the AI maturity baseline, the industry benchmark and the prioritized roadmap inside the Impact Platform.',
      },
      // `#contact` rather than the Contact Us page, since 2026-08-30: the
      // closing form now sits at the bottom of every document (see
      // `components/ContactBand.tsx`), so this scrolls the reader down to it.
      ctas: [{ label: 'Talk with an expert', href: '#contact' }],
      check:
        'THE FILM IS HERE, AND IT IS 47 MB. Uploaded on 2026-08-30, which '
        + 'closes a request first made on the 27th. What is left is its weight: '
        + 'this is roughly six times the size worth serving, and every reader who '
        + 'scrolls to this panel downloads all of it — on a phone, on their own '
        + 'data. Nothing is fetched until the panel is actually on screen '
        + '(`components/Video.tsx`), which is what makes 47 MB survivable '
        + 'rather than reckless, but it is not a fix.\n\n'
        + 'Re-encode it and upload the smaller one: H.264, 1080p or 720p, CRF '
        + 'around 23, AAC audio — the same film usually lands between 6 and 10 MB '
        + 'without a visible difference on a page where it plays in a column about '
        + '400px wide. Then swap the URL on this section; nothing else changes. '
        + 'Worth doing before this page goes to production, not after.',
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Why AIMI Matters',
      sub: 'Executives know AI is important. What they lack is:',
      points: [
        'a clear starting point',
        'a shared maturity baseline',
        'a consistent way to prioritize AI initiatives',
        'a roadmap tied to business value',
        'a system that keeps teams aligned',
      ],
      outro: ['AIMI solves each one.', 'Looking for a practical way to move forward with AI?'],
      photo: {
        src: '/photo/boardroom-daimi-target-state-applause.webp',
        alt: 'A leadership team applauding in front of a screen showing their current and target state.',
      },
      // `#contact` rather than the Contact Us page, since 2026-08-30: the
      // closing form now sits at the bottom of every document (see
      // `components/ContactBand.tsx`), so this scrolls the reader down to it.
      ctas: [{ label: 'Talk with an expert', href: '#contact' }],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'How It Works',
      points: [
        'Step 1. Assess. Review your current AI maturity with guided workshops and benchmarks.',
        'Step 2. Prioritize. Identify the highest value AI moves using our recommendation engine.',
        'Step 3. Execute. Track progress inside the Impact Platform and stay aligned through quarterly business reviews.',
      ],
      photo: {
        src: '/photo/impact-platform-target-state.webp',
        alt: 'The Impact Platform’s target state screen: the index score, the scores by dimension and the industry benchmark side by side.',
      },
      ctas: [{ label: 'Request a Demo', href: '#contact' }],
    },
    // The comparison the live page makes with a red-cross column and a
    // green-tick column. Two lists here, one above the other, in this site's
    // own bullet: the words are the reference's and the headings carry which
    // side is which, so nothing about the argument depends on the marks.
    {
      kind: 'list',
      heading: 'How AIMI Is Different from Traditional Consulting',
      sub: 'Traditional Consulting',
      items: [
        'Long timelines',
        'Large teams',
        'Slide based reporting',
        'Limited alignment',
        'No ongoing visibility',
      ],
    },
    {
      kind: 'list',
      sub: 'AIMI',
      items: [
        'One expert consultant',
        'Minimal disruption',
        'Instant online reporting',
        'AI Wizards guiding every step',
        'Clear priorities with rationale',
        'Real time visibility in the Impact Platform',
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Make AI Progress Visible and Measurable',
      body: [
        'AIMI helps you understand your AI maturity and define the right priorities. The Impact Platform ensures those priorities turn into sustained execution.',
        'Inside the platform, leaders track AI initiatives, monitor progress against the roadmap, and keep teams aligned around shared goals. Real time dashboards, AI Wizards, and structured reviews make it easier to move from assessment to execution and maintain momentum as AI scales.',
        'Together, AIMI and the Impact Platform provide clarity, direction, and continuous visibility across your AI transformation.',
      ],
      photo: {
        src: '/photo/impact-platform-daimi-dashboard.webp',
        alt: 'The Impact Platform home screen on a laptop: the current score, annual progress, milestones and the dimension runs in flight.',
      },
      ctas: [{ label: 'Explore the Impact Platform', href: PLATFORM }],
    },
    // The three customer quotes, in the Hall of Fame card with a tab per
    // company — asked for with this page (*"kaydırmalı testimoniallar proven,
    // yazaki, kordsa"*). They are the same words, the same marks and the same
    // portraits `content/testimonials.ts` and the DAIMI page already carry for
    // those three companies, so the pages cannot drift apart.
    {
      kind: 'quotes',
      variant: 'fame',
      items: [
        {
          quote:
            'Working with Digitopia has been a transformative experience for PROVEN. The Digital Maturity Index provided the clarity and structure we needed to assess our capabilities, while the interactive sessions delivered valuable, actionable insights. Benchmarking against industry peers gave us clear direction, ensuring smarter investments and stronger alignment across leadership. This initiative has set a solid foundation for our digital transformation, and we’re excited to build on this momentum.',
          source: 'Johann Heaven, CEO, PROVEN',
          company: 'PROVEN',
          logo: { name: 'PROVEN', src: '/logo/provencard.png' },
          portrait: { src: '/photo/portrait-johann-heaven.png', alt: 'Johann Heaven' },
        },
        {
          quote:
            'Digitopia’s Digital Maturity Index gave us the clarity about the positive progress we have made recently in creating business value with digitalization. We also had a chance to benchmark ourselves with the industry peers. Digitopia’s insightful recommendations helped us fine tune and confirm our strategy. This partnership has been a key milestone in our journey to innovation and digitalization.',
          source: 'Tekin Gülşen, VP of IT Y-EMEA and Global Applications',
          company: 'Yazaki',
          logo: { name: 'Yazaki', src: '/logo/yazakicard.png' },
          portrait: { src: '/photo/portrait-tekin-gulsen.png', alt: 'Tekin Gülşen' },
        },
        {
          quote:
            'Digitopia’s DMI assessments have helped us create clear, actionable digital roadmaps while staying on track. Their insights have been invaluable, guiding us to focus on critical areas like AI long before it became a major topic. As part of Sabancı Holding, it’s a significant advantage that other group companies also use Digitopia’s assessments, enabling a comprehensive view of digital maturity across the group. Digitopia is not just a partner; they play an essential role in shaping and driving our digital strategy.',
          source: 'Fatih Akar, Director of Global Information Technologies',
          company: 'Kordsa',
          logo: { name: 'Kordsa', src: '/logo/kordsacard.png' },
          portrait: { src: '/photo/portrait-fatih-akar.png', alt: 'Fatih Akar' },
        },
      ],
      cta: { label: 'Read the full stories', href: LINKS.hallOfFame },
    },
    /*
      No closing dark CTA band here, asked for 2026-08-30: "form olan herhangi
      bir sayfada formun hemen üstünde genelde bu siyah kutu oluyor, bunlar
      olmasın". The closing form already sits at the foot of this page, so a
      band whose button pointed at `#contact` was asking the reader to scroll to
      something they could already see. The "world's leading" superlative that
      used to carry a `check:` here went with it — it is no longer on the page.
    */
    {
      kind: 'prose',
      align: 'center',
      heading: 'Essential Reads on AI Maturity',
      sub: 'Token economics, delayed decisions, the four AI transformation paths. The thinking behind AIMI, article by article.',
      cta: { label: 'Read Impact Labs', href: LINKS.reports },
      check:
        'The reference ends on a scrolling row of four blog cards with their own '
        + 'artwork, pulled from the archive. This site has no post-carousel '
        + 'section, so the band is the heading and one link to where those '
        + 'articles live. If the row itself is wanted here, it is a new section '
        + 'kind rather than a copy change.',
    },
  ],
}
